import { makeAutoObservable, action, runInAction } from 'mobx'
import { ThemeColor, themeColors } from './theme.types'
import { DEFAULT_THEME } from './themes/Default'
import { mapCSSVariablesToThemeColor } from './themeMapping'
import { ObjectUtils } from '@/utils/object'
import { generateThemeVars } from './utils'

export class ConstructorTheme {
  theme: { [key in ThemeColor]: string } = { ...DEFAULT_THEME }
  name: string = `theme_${Math.floor(Math.random() * 10000)}.json`

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true })
  }

  getColor(key: ThemeColor) {
    const color = this.theme[key]
    if (color.length !== 0) {
      return { value: color, visibleValue: color }
    }

    const cssVar = mapCSSVariablesToThemeColor[key]

    if (typeof window !== 'undefined') {
      const styles = window.getComputedStyle(document.body)

      return {
        value: styles.getPropertyValue(cssVar).trim(),
        visibleValue: `var(${cssVar})`,
      }
    }

    return { value: '', visibleValue: `var(${cssVar})` }
  }

  setColor(key: ThemeColor, value: string) {
    this.theme[key] = value

    // Перегенерировать тему
    this.applyTheme()
  }

  // Экспорт в JSON-файл
  exportToFile() {
    const theme = this.getCurrentTheme()
    const dataStr = JSON.stringify(theme, null, 2)
    const blob = new Blob([dataStr], { type: 'application/json' })
    const url = URL.createObjectURL(blob)

    // Для браузера: инициировать скачивание
    const a = document.createElement('a')
    a.href = url
    a.download = this.name
    a.click()
    URL.revokeObjectURL(url)
  }

  // Импорт из JSON-файла
  importFromFile(file: File) {
    const reader = new FileReader()
    reader.onload = action((e) => {
      try {
        const json = JSON.parse(e.target?.result as string)
        // Можно добавить валидацию структуры
        runInAction(() => {
          this.theme = { ...this.theme, ...json }(json)
          this.name = file.name
          this.applyTheme()
        })
      } catch (err) {
        // обработка ошибки
        console.log('Invalid theme file', err)
      }
    })
    reader.readAsText(file)
  }

  applyTheme() {
    const body = document.body

    const currentTheme = this.getCurrentTheme()
    const typeTheme = document.body.classList.contains('darkTheme')
      ? 'dark'
      : 'light'
    const generatedVars = generateThemeVars(currentTheme, typeTheme)

    ObjectUtils.entries(generatedVars).forEach(([key, value]) => {
      body.style.setProperty(key, value)
    })
  }

  getCurrentTheme() {
    return themeColors.reduce((acc, key) => {
      if (this.theme[key] && this.theme[key].length !== 0) {
        acc[key] = this.theme[key]
        return acc
      }

      const cssVar = mapCSSVariablesToThemeColor[key]
      const styles = window.getComputedStyle(document.body)
      acc[key] = styles.getPropertyValue(cssVar).trim()
      return acc
    }, {} as { [key in ThemeColor]: string })
  }

  setName(event: React.ChangeEvent<HTMLInputElement>) {
    this.name = event.target.value
  }
}
